import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Activity, Server, AlertTriangle, Check, X, Loader2,
  RefreshCw, ChevronDown, ArrowRight
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

function HealthBadge({ value, thresholdWarn = 70, thresholdCrit = 85 }) {
  if (value === null || value === undefined) return <span className="text-xs text-slate-400">--</span>;
  const color = value > thresholdCrit ? 'text-red-600 bg-red-50 border-red-200' 
    : value > thresholdWarn ? 'text-amber-600 bg-amber-50 border-amber-200' 
    : 'text-emerald-600 bg-emerald-50 border-emerald-200';
  return (
    <span className={`inline-flex px-2 py-0.5 rounded text-xs font-semibold border ${color}`}>
      {value.toFixed(1)}%
    </span>
  );
}

function UptimeBadge({ value }) {
  if (value === null || value === undefined) return <span className="text-xs text-slate-400">--</span>;
  const color = value >= 99.9 ? 'text-emerald-600' : value >= 99 ? 'text-amber-600' : 'text-red-600';
  return <span className={`text-xs font-bold ${color}`}>{value.toFixed(2)}%</span>;
}

function ServerHealthRow({ server }) {
  const cpu = server.cpu || {};
  const memory = server.memory || {};
  const disks = server.disks || [];
  const maxDisk = disks.length > 0 ? Math.max(...disks.map(d => d.used_pct)) : null;

  return (
    <tr className="border-b border-slate-100 hover:bg-slate-50/50 transition-colors">
      <td className="py-2.5 px-3">
        <span className="font-mono text-xs font-medium text-slate-800">{server.server_name}</span>
      </td>
      <td className="py-2.5 px-3 text-center">
        <HealthBadge value={cpu.available ? cpu.average : null} />
      </td>
      <td className="py-2.5 px-3 text-center">
        <HealthBadge value={memory.available ? memory.average : null} />
      </td>
      <td className="py-2.5 px-3 text-center">
        <HealthBadge value={maxDisk} thresholdWarn={75} thresholdCrit={90} />
      </td>
      <td className="py-2.5 px-3 text-center">
        <UptimeBadge value={server.availability_percent} />
      </td>
    </tr>
  );
}

function CustomerHealthCard({ customer, healthData, loading }) {
  const [expanded, setExpanded] = useState(false);
  const navigate = useNavigate();
  
  const servers = healthData?.servers || [];
  const hasMetrics = healthData?.site24x7_connected;
  const serverCount = servers.length;
  
  // Calculate aggregated health
  const cpuValues = servers.filter(s => s.cpu?.available).map(s => s.cpu.average);
  const memValues = servers.filter(s => s.memory?.available).map(s => s.memory.average);
  const avgCpu = cpuValues.length ? cpuValues.reduce((a, b) => a + b, 0) / cpuValues.length : null;
  const avgMem = memValues.length ? memValues.reduce((a, b) => a + b, 0) / memValues.length : null;
  const avgUptime = servers.length ? servers.reduce((a, s) => a + (s.availability_percent || 100), 0) / servers.length : null;
  
  // Overall status
  const hasWarning = cpuValues.some(v => v > 70) || memValues.some(v => v > 70);
  const hasCritical = cpuValues.some(v => v > 85) || memValues.some(v => v > 85);
  const statusColor = hasCritical ? 'border-l-red-500' : hasWarning ? 'border-l-amber-500' : 'border-l-emerald-500';
  const statusDot = hasCritical ? 'bg-red-500' : hasWarning ? 'bg-amber-500' : 'bg-emerald-500';

  return (
    <div data-testid={`health-card-${customer.id}`} className={`bg-white rounded-lg border border-slate-200 border-l-4 ${statusColor} overflow-hidden transition-shadow hover:shadow-md`}>
      <div 
        className="p-4 cursor-pointer flex items-center justify-between"
        onClick={() => serverCount > 0 && setExpanded(!expanded)}
      >
        <div className="flex items-center gap-3">
          <div className={`w-2.5 h-2.5 rounded-full ${statusDot} ${hasCritical ? 'animate-pulse' : ''}`} />
          <div>
            <h3 className="font-semibold text-slate-900 text-sm">{customer.name}</h3>
            <p className="text-xs text-slate-500">
              {loading ? 'Loading...' : hasMetrics ? `${serverCount} server${serverCount !== 1 ? 's' : ''} monitored` : 'No metrics available'}
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-4">
          {loading ? (
            <Loader2 className="w-4 h-4 animate-spin text-slate-400" />
          ) : hasMetrics && serverCount > 0 ? (
            <>
              <div className="text-center hidden sm:block">
                <p className="text-[10px] text-slate-400 uppercase tracking-wider">CPU</p>
                <HealthBadge value={avgCpu} />
              </div>
              <div className="text-center hidden sm:block">
                <p className="text-[10px] text-slate-400 uppercase tracking-wider">MEM</p>
                <HealthBadge value={avgMem} />
              </div>
              <div className="text-center hidden md:block">
                <p className="text-[10px] text-slate-400 uppercase tracking-wider">UPTIME</p>
                <UptimeBadge value={avgUptime} />
              </div>
              <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${expanded ? 'rotate-180' : ''}`} />
            </>
          ) : (
            <span className="text-xs text-slate-400">No data</span>
          )}
        </div>
      </div>

      {expanded && servers.length > 0 && (
        <div className="border-t border-slate-100">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-slate-50">
                <th className="text-left py-2 px-3 font-semibold text-slate-500">Server</th>
                <th className="text-center py-2 px-3 font-semibold text-slate-500">CPU</th>
                <th className="text-center py-2 px-3 font-semibold text-slate-500">Memory</th>
                <th className="text-center py-2 px-3 font-semibold text-slate-500">Disk</th>
                <th className="text-center py-2 px-3 font-semibold text-slate-500">Uptime</th>
              </tr>
            </thead>
            <tbody>
              {servers.map((s, i) => <ServerHealthRow key={i} server={s} />)}
            </tbody>
          </table>
          <div className="px-4 py-2 border-t border-slate-100 flex justify-end">
            <button 
              data-testid={`generate-report-${customer.id}`}
              onClick={(e) => { e.stopPropagation(); navigate('/generate'); }}
              className="text-xs text-primary font-medium flex items-center gap-1 hover:underline"
            >
              Generate Report <ArrowRight className="w-3 h-3" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function DashboardPage() {
  const [customers, setCustomers] = useState([]);
  const [healthData, setHealthData] = useState({});
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [site24x7Status, setSite24x7Status] = useState(null);

  const fetchCustomers = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/customers`);
      setCustomers(res.data);
      return res.data;
    } catch (err) {
      console.error('Failed to fetch customers:', err);
      return [];
    }
  };

  const fetchHealthForCustomer = async (customer) => {
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth() + 1;
    try {
      const res = await axios.post(`${API_URL}/api/reports/preview`, {
        customer_id: customer.id,
        year,
        month
      });
      return { id: customer.id, data: res.data };
    } catch (err) {
      return { id: customer.id, data: null };
    }
  };

  const fetchAllHealth = async (customerList) => {
    setRefreshing(true);
    // Fetch in batches of 3 to avoid overwhelming the API
    const results = {};
    for (let i = 0; i < customerList.length; i += 3) {
      const batch = customerList.slice(i, i + 3);
      const batchResults = await Promise.all(batch.map(c => fetchHealthForCustomer(c)));
      batchResults.forEach(r => { results[r.id] = r.data; });
      setHealthData(prev => ({ ...prev, ...Object.fromEntries(batchResults.map(r => [r.id, r.data])) }));
    }
    setRefreshing(false);
  };

  useEffect(() => {
    const init = async () => {
      const [custList, statusRes] = await Promise.all([
        fetchCustomers(),
        axios.get(`${API_URL}/api/status`).then(r => r.data?.site24x7).catch(() => null)
      ]);
      setSite24x7Status(statusRes);
      setLoading(false);
      if (custList.length > 0) {
        fetchAllHealth(custList);
      }
    };
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRefresh = () => {
    if (!refreshing && customers.length > 0) {
      setHealthData({});
      fetchAllHealth(customers);
    }
  };

  // Summary stats
  const allServers = Object.values(healthData).filter(Boolean).flatMap(d => d.servers || []);
  const totalServers = allServers.length;
  const healthyServers = allServers.filter(s => (s.cpu?.available ? s.cpu.average < 70 : true) && (s.memory?.available ? s.memory.average < 70 : true)).length;
  const warningServers = totalServers - healthyServers;
  const avgUptime = allServers.length ? (allServers.reduce((a, s) => a + (s.availability_percent || 100), 0) / allServers.length) : 0;
  const customersWithData = Object.values(healthData).filter(d => d?.site24x7_connected).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div data-testid="dashboard-page" className="max-w-5xl mx-auto">
      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-lg border border-slate-200 p-4">
          <div className="flex items-center gap-2 mb-1">
            <Server className="w-4 h-4 text-primary" />
            <span className="text-xs text-slate-500 uppercase tracking-wider">Total Servers</span>
          </div>
          <p data-testid="total-servers" className="text-2xl font-bold text-slate-900">{totalServers}</p>
          <p className="text-xs text-slate-500">{customersWithData}/{customers.length} customers monitored</p>
        </div>
        
        <div className="bg-white rounded-lg border border-slate-200 p-4">
          <div className="flex items-center gap-2 mb-1">
            <Check className="w-4 h-4 text-emerald-500" />
            <span className="text-xs text-slate-500 uppercase tracking-wider">Healthy</span>
          </div>
          <p data-testid="healthy-servers" className="text-2xl font-bold text-emerald-600">{healthyServers}</p>
          <p className="text-xs text-slate-500">CPU & Memory under 70%</p>
        </div>
        
        <div className="bg-white rounded-lg border border-slate-200 p-4">
          <div className="flex items-center gap-2 mb-1">
            <AlertTriangle className="w-4 h-4 text-amber-500" />
            <span className="text-xs text-slate-500 uppercase tracking-wider">Warnings</span>
          </div>
          <p data-testid="warning-servers" className="text-2xl font-bold text-amber-600">{warningServers}</p>
          <p className="text-xs text-slate-500">Needs attention</p>
        </div>
        
        <div className="bg-white rounded-lg border border-slate-200 p-4">
          <div className="flex items-center gap-2 mb-1">
            <Activity className="w-4 h-4 text-primary" />
            <span className="text-xs text-slate-500 uppercase tracking-wider">Avg Uptime</span>
          </div>
          <p data-testid="avg-uptime" className="text-2xl font-bold text-slate-900">{avgUptime > 0 ? `${avgUptime.toFixed(1)}%` : '--'}</p>
          <p className="text-xs text-slate-500">This month</p>
        </div>
      </div>

      {/* Site24x7 Connection Status */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          {site24x7Status?.connected ? (
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
              Site24x7 Connected ({site24x7Status.groups} groups)
            </span>
          ) : (
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-red-50 text-red-700 border border-red-200">
              <X className="w-3 h-3" />
              Site24x7 Not Connected
            </span>
          )}
          {refreshing && <span className="text-xs text-slate-500 ml-2">Refreshing data...</span>}
        </div>
        <button
          data-testid="refresh-dashboard-btn"
          onClick={handleRefresh}
          disabled={refreshing}
          className="flex items-center gap-1.5 text-sm text-primary font-medium hover:text-primary-600 disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Customer Health Cards */}
      <div className="space-y-3">
        {customers.map(customer => (
          <CustomerHealthCard
            key={customer.id}
            customer={customer}
            healthData={healthData[customer.id]}
            loading={refreshing && !healthData[customer.id]}
          />
        ))}
      </div>
    </div>
  );
}
